// =====================================================================
// Assessment Center — Instructor Panel
// Create Question page. Also handles editing (route: .../questions/:id/edit)
// so "Create Question" and "Edit Question" share one implementation,
// per the existing schema's two question types (multiple_choice, essay).
// =====================================================================
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  Select,
  Textarea,
  PageHeader,
  Spinner,
} from "../../../shared/components/ui";
import {
  createQuestion,
  getQuestion,
  listQuestionBanks,
  updateQuestion,
  type QuestionInput,
  type MultipleChoiceAnswerInput,
} from "../lib/mock-api";
import type { QuestionBank, QuestionDifficulty, QuestionType } from "../../../shared/types/db";

const emptyOption = (): MultipleChoiceAnswerInput => ({ answer_text: "", is_correct: false });

export default function CreateQuestionPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [banks, setBanks] = useState<QuestionBank[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState<QuestionStatusAction | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [bankId, setBankId] = useState("");
  const [type, setType] = useState<QuestionType>("multiple_choice");
  const [prompt, setPrompt] = useState("");
  const [difficulty, setDifficulty] = useState<QuestionDifficulty>("medium");
  const [points, setPoints] = useState(1);
  const [options, setOptions] = useState<MultipleChoiceAnswerInput[]>([
    emptyOption(),
    emptyOption(),
  ]);
  const [modelAnswer, setModelAnswer] = useState("");
  const [rubric, setRubric] = useState("");
  const [minWords, setMinWords] = useState<number | "">("");
  const [maxWords, setMaxWords] = useState<number | "">("");

  useEffect(() => {
    listQuestionBanks().then((b) => {
      setBanks(b);
      setBankId((prev) => prev || b[0]?.id || "");
    });
  }, []);

  useEffect(() => {
    if (!id) return;
    getQuestion(id).then((q) => {
      if (!q) {
        setError("Question not found.");
        setLoading(false);
        return;
      }
      setBankId(q.question_bank_id);
      setType(q.type);
      setPrompt(q.prompt);
      setDifficulty(q.difficulty);
      setPoints(q.default_points);
      if (q.type === "multiple_choice" && q.answers) {
        setOptions(
          q.answers.map((a) => ({ answer_text: a.answer_text, is_correct: a.is_correct }))
        );
      }
      if (q.type === "essay" && q.essay) {
        setModelAnswer(q.essay.model_answer ?? "");
        setRubric(q.essay.grading_rubric ?? "");
        setMinWords(q.essay.min_words ?? "");
        setMaxWords(q.essay.max_words ?? "");
      }
      setLoading(false);
    });
  }, [id]);

  function updateOption(index: number, patch: Partial<MultipleChoiceAnswerInput>) {
    setOptions((prev) => prev.map((o, i) => (i === index ? { ...o, ...patch } : o)));
  }

  function markCorrect(index: number) {
    setOptions((prev) => prev.map((o, i) => ({ ...o, is_correct: i === index })));
  }

  function addOption() {
    setOptions((prev) => [...prev, emptyOption()]);
  }

  function removeOption(index: number) {
    setOptions((prev) => prev.filter((_, i) => i !== index));
  }

  function validate(): string | null {
    if (!bankId) return "Choose a question bank.";
    if (!prompt.trim()) return "Write the question prompt.";
    if (points <= 0) return "Points must be greater than zero.";
    if (type === "multiple_choice") {
      const filled = options.filter((o) => o.answer_text.trim());
      if (filled.length < 2) return "Add at least two answer options.";
      if (!filled.some((o) => o.is_correct)) return "Mark one option as the correct answer.";
    }
    if (type === "essay" && minWords !== "" && maxWords !== "" && minWords > maxWords) {
      return "Minimum word count can't exceed the maximum.";
    }
    return null;
  }

  type QuestionStatusAction = "draft" | "published";

  async function handleSave(status: QuestionStatusAction) {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setSaving(status);
    try {
      const input: QuestionInput = {
        question_bank_id: bankId,
        type,
        prompt: prompt.trim(),
        difficulty,
        default_points: points,
        status,
        ...(type === "multiple_choice"
          ? { answers: options.filter((o) => o.answer_text.trim()) }
          : {
              model_answer: modelAnswer.trim() || undefined,
              grading_rubric: rubric.trim() || undefined,
              min_words: minWords === "" ? null : minWords,
              max_words: maxWords === "" ? null : maxWords,
            }),
      };
      if (isEdit && id) {
        await updateQuestion(id, input);
      } else {
        await createQuestion(input);
      }
      navigate("/instructor/assessment-center/question-bank");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save the question.");
    } finally {
      setSaving(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-20 text-slate-500">
        <Spinner /> Loading question…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title={isEdit ? "Edit Question" : "Create Question"}
        subtitle={
          isEdit
            ? "Update the prompt, options, or grading details."
            : "Add a multiple choice or essay question to a question bank."
        }
      />

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card className="p-5">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Question bank" required>
            <Select value={bankId} onChange={(e) => setBankId(e.target.value)}>
              {banks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.title}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Question type" required>
            <Select
              value={type}
              disabled={isEdit}
              onChange={(e) => setType(e.target.value as QuestionType)}
            >
              <option value="multiple_choice">Multiple Choice</option>
              <option value="essay">Essay</option>
            </Select>
          </Field>
        </div>

        <div className="mt-5">
          <Field label="Prompt" required>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Type the question text students will see…"
            />
          </Field>
        </div>

        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <Field label="Difficulty">
            <Select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value as QuestionDifficulty)}
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </Select>
          </Field>
          <Field label="Default points" required>
            <Input
              type="number"
              min={0.5}
              step={0.5}
              value={points}
              onChange={(e) => setPoints(Number(e.target.value))}
            />
          </Field>
        </div>
      </Card>

      {type === "multiple_choice" ? (
        <Card className="mt-5">
          <CardHeader
            title="Answer options"
            subtitle="Add options and mark the correct one."
          />
          <div className="space-y-3 p-5">
            {options.map((opt, i) => (
              <div key={i} className="flex items-center gap-3">
                <input
                  type="radio"
                  name="correct-option"
                  checked={opt.is_correct}
                  onChange={() => markCorrect(i)}
                  aria-label={`Mark option ${i + 1} as correct`}
                  className="h-4 w-4 accent-navy-700"
                />
                <Input
                  value={opt.answer_text}
                  onChange={(e) => updateOption(i, { answer_text: e.target.value })}
                  placeholder={`Option ${i + 1}`}
                  className="flex-1"
                />
                <button
                  type="button"
                  onClick={() => removeOption(i)}
                  disabled={options.length <= 2}
                  className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                  aria-label={`Remove option ${i + 1}`}
                >
                  ✕
                </button>
              </div>
            ))}
            <Button variant="secondary" type="button" onClick={addOption}>
              + Add option
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="mt-5">
          <CardHeader title="Essay grading details" subtitle="Optional, but helps reviewers grade consistently." />
          <div className="space-y-5 p-5">
            <Field label="Model answer" hint="Shown only to instructors during review.">
              <Textarea value={modelAnswer} onChange={(e) => setModelAnswer(e.target.value)} />
            </Field>
            <Field label="Grading rubric">
              <Textarea value={rubric} onChange={(e) => setRubric(e.target.value)} />
            </Field>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Minimum words">
                <Input
                  type="number"
                  min={0}
                  value={minWords}
                  onChange={(e) => setMinWords(e.target.value === "" ? "" : Number(e.target.value))}
                />
              </Field>
              <Field label="Maximum words">
                <Input
                  type="number"
                  min={0}
                  value={maxWords}
                  onChange={(e) => setMaxWords(e.target.value === "" ? "" : Number(e.target.value))}
                />
              </Field>
            </div>
          </div>
        </Card>
      )}

      <div className="mt-6 flex justify-end gap-2">
        <Button
          variant="secondary"
          onClick={() => navigate("/instructor/assessment-center/question-bank")}
        >
          Cancel
        </Button>
        <Button variant="secondary" disabled={!!saving} onClick={() => handleSave("draft")}>
          {saving === "draft" ? <Spinner /> : null} Save as Draft
        </Button>
        <Button disabled={!!saving} onClick={() => handleSave("published")}>
          {saving === "published" ? <Spinner /> : null} {isEdit ? "Save & Publish" : "Publish Question"}
        </Button>
      </div>
    </div>
  );
}
